import React, { FC, useContext, useEffect, useState } from 'react';
import styles from './speciesAndZonesComponent.module.scss';
import { EspeciesPeligro, ObraProperties, ZonaProteccion } from '../../../../../../types/ObraDetails';
import { SelectOption } from '../../../../../../types/Option';
import { ValidationError } from '../../../../../../types/ValidationError';
import extinctionDanger from '../../../../../../assets/extinctionDanger.svg';
import culturalProtection from '../../../../../../assets/culturalProtection.svg';
import parse from 'html-react-parser';
import { SaveContext } from '../../../../../../context/SaveProvider';
import { UserModeContext } from '../../../../../../context/UserModeProvider';
import { t } from 'i18next';
import { AuthContext } from '../../../../../../services/authContextProvider';
import TitleHelp from '../../../../components/titleHelp/titleHelp';
import ObrasAPI from '../../../../../../services/APIObras';
import TitleValue from '../../../../components/titleValue/titleValue';
import DeleteButton from '../../../../components/deleteButton/deleteButton';
import SimpleSelect from '../../../../components/simpleSelect/simpleSelect';


interface SpeciesAndZonesComponentProps {
  obra: ObraProperties;
  data: EspeciesPeligro[] | ZonaProteccion[];
  scrollDown: () => void;
  type: 'species' | 'zones';
  errors?: ValidationError[];
}

/* eslint-disable @typescript-eslint/no-non-null-assertion */
const SpeciesAndZonesComponent: FC<SpeciesAndZonesComponentProps> = (props) => {
  const { obra, data, type, scrollDown, errors } = props;
  const authContext = useContext(AuthContext);
  const saveContext = useContext(SaveContext);
  const userModeContext = useContext(UserModeContext);
  const [dataState, setDataState] = useState(data);
  const [options, setOptions] = useState<SelectOption[]>([]);

  const NO_RESULTS = [{ nombre: `${t('input.noResults')}`, id: 0 }];
  const TYPE_SPECIES = type === 'species';

  useEffect(() => {
    const api = ObrasAPI.getInstance();
    const apiCall = api.getProtectionZones();
    apiCall.then(
      (results: SelectOption[]) => {
        results = results.length ? results : NO_RESULTS;
        const sortedResults = results.sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
        setOptions(sortedResults);
      }
    ).catch((error) => {
      console.log(error);
    });
  }, []);

  useEffect(() => {
    setDataState(data);
  }, [data]);


  const handleAddItem = () => {
    const newZone = { id: undefined, proteccion: { id: 0, nombre: '' }, nombre: '' };
    setDataState(currentData => TYPE_SPECIES
      ? currentData
      : [...(currentData as ZonaProteccion[]), newZone]);
    if (obra.localizacion?.zonasProteccion)
      obra.localizacion.zonasProteccion.push(newZone);
    else
      obra.localizacion.zonasProteccion = [newZone];

    saveContext.handleChangeSave(true);
    scrollDown();
  };

  return (
    <div className={styles.editExtinctionSpecies}>
      <TitleHelp
        id={TYPE_SPECIES ? 'extinctionspecies' : 'protectionzones'}
        value={
          TYPE_SPECIES
            ? t('details.location.extinctionSpecies')
            : t('details.location.protectionZones')
        }
        help={
          TYPE_SPECIES
            ? t('help.localization.extinctionSpecies')
            : t('help.localization.protectedZones')
        }
        {...(!TYPE_SPECIES && {
          addItemText: String(t('details.location.addZone')),
          handleAddItem,
        })}
      />
      <div className={styles.extinctionSpeciesProtectionZonesContainer}>
        {dataState.length > 0 ?
          <div>
            {dataState.map((_entry, i: number) => {
              const handleChangeName = (s: string) => {
                obra.localizacion.zonasProteccion[i].nombre = s;
                saveContext.handleChangeSave(true);
              };

              const handleChangeValue = (o: SelectOption) => {
                obra.localizacion.zonasProteccion[i].proteccion = o;
                saveContext.handleChangeSave(true);
              };

              const handleDeleteItem = () => {
                obra.localizacion.zonasProteccion.splice(i, 1);
                setDataState([...obra.localizacion.zonasProteccion]);
                saveContext.handleChangeSave(true);
              };

              return (
                <div key={i.toString()} className={styles.row}>
                  <div className={styles.column1}>
                    <TitleValue
                      identifier={`${TYPE_SPECIES ? 'extinctionSpeciesTable' : 'protectionZoneTable'}-title${i}`}
                      title={t('details.location.name')}
                      error={errors && errors.length > 0 ? TYPE_SPECIES ? !obra.localizacion.especiesPeligro[i].nombre : !obra.localizacion.zonasProteccion[i]?.nombre : false}
                      value={
                        TYPE_SPECIES ?
                          obra.localizacion.especiesPeligro[i]?.nombre :
                          obra.localizacion.zonasProteccion[i]?.nombre
                      }
                      editable={!TYPE_SPECIES}
                      handleChange={handleChangeName}
                      onlyView={TYPE_SPECIES}
                    />
                  </div>
                  <div className={styles.column2}>
                    {TYPE_SPECIES ?
                      <TitleValue
                        identifier={`extinctionSpeciesTable-${i}`}
                        title={t('details.location.dangerLevel')}
                        value={obra.localizacion.especiesPeligro[i]?.nivel.nombre}
                        valueMaxLength={25}
                        onlyView={true}
                      />
                      :
                      <SimpleSelect
                        id={`protectionZoneTable-${i}`}
                        options={options}
                        error={errors && errors.length > 0 ? !obra.localizacion.zonasProteccion[i]?.proteccion?.nombre : false}
                        value={obra.localizacion.zonasProteccion[i]?.proteccion}
                        label={t('details.location.protection')}
                        handleChangeValue={handleChangeValue}
                        editable={true}
                      />
                    }
                  </div>

                  {!TYPE_SPECIES &&
                    <DeleteButton id={`deleteBtn${i}`} onClick={handleDeleteItem} />
                  }
                </div>
              );
            })}
          </div>
          :
          <div id={`${TYPE_SPECIES ? 'extinctionDanger' : 'protection'}-nodata`} className={styles.noDataPic}>
            <img src={`${TYPE_SPECIES ? extinctionDanger : culturalProtection}`} />
            {authContext.can('can_save_draft') ?
              <>
                {userModeContext.userMode === 'viewer' &&
                  <p>{parse(t(`details.location.${TYPE_SPECIES
                    ? 'noData'
                    : 'changeUserAddCulturalProtection'}`))}</p>}
                {userModeContext.userMode === 'editor' &&
                  <p>{parse(t(`details.location.${TYPE_SPECIES
                    ? 'noData'
                    : 'addCulturalProtection'}`))}</p>}
              </>
              :
              <p>{t('details.location.noData')}</p>
            }
          </div>
        }
      </div>
    </div>
  );
};

export default SpeciesAndZonesComponent;
